import React, { useState, useEffect } from 'react';
import {
  Account,
  CountriesList,
  fetchCountriesList,
  AccountGroup,
} from '@amzn/aac-chrome-internal-api';
import { createContext, useContext } from 'react';
import { RegionSwitcherPublisher } from 'src/commons/utils/metrics';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { logger } from 'src/commons/utils/logger';
import { useAccountDropdownContext } from '../AccountDropdown/AccountDropdownProvider';
import { Region } from '@amzn/d16-g-adsp-region-switcher-ui/dist/src/types/Region';

export type RegionSwitcherAccount = {
  entityId: string;
  url: string;
  regionId: Region;
  cfid?: string;
  marketplaceId: string;
};

interface RegionSwitcherContextValue {
  accounts: RegionSwitcherAccount[];
  currentAccount: RegionSwitcherAccount | null;
}

export const RegionSwitcherContext = createContext<RegionSwitcherContextValue>({
  accounts: [],
  currentAccount: null,
});

export const useRegionSwitcherContext = () => {
  const context = useContext(RegionSwitcherContext);
  if (context === undefined) {
    throw new Error(
      'useRegionSwitcherContext must be used within a RegionSwitcherProvider',
    );
  }
  return context;
};

const findCurrentAccountsCountriesController = (
  countryListAccounts: CountriesList,
  currentMarketplaceId: string,
): [RegionSwitcherAccount | undefined, RegionSwitcherAccount[] | undefined] => {
  const accounts = transformRegionSwitcherAccounts(countryListAccounts);
  let currentAccount: RegionSwitcherAccount | undefined;
  let regionalAccounts: RegionSwitcherAccount[] | undefined;
  for (const account of accounts) {
    if (account.marketplaceId === currentMarketplaceId) {
      currentAccount = account;
      regionalAccounts = accounts;
      break;
    }
  }
  return [currentAccount, regionalAccounts];
};

// TODO: Deprecate when the weblab is fully running
const findCurrentAccountNavStateController = (
  currentEntityId: string,
  currentMarketplaceId: string,
  accounts: AccountGroup[],
): [Account | undefined, Account[] | undefined] => {
  let currentAccount: Account | undefined;
  let regionalAccounts: Account[] | undefined;

  for (const account of accounts) {
    for (const subAccount of account.accounts) {
      if (subAccount.entityId === currentEntityId) {
        if (subAccount.nestedAccounts) {
          regionalAccounts = subAccount.nestedAccounts;
          for (const nestedAccount of subAccount.nestedAccounts) {
            if (nestedAccount.marketplaceId === currentMarketplaceId) {
              currentAccount = nestedAccount;
              break;
            }
          }
          break;
        }
      }
    }
  }
  return [currentAccount, regionalAccounts];
};

const determineAccounts = (
  countryListAccounts: CountriesList,
  currentMarketplaceId: string,
  currentEntityId: string,
  navStateAccounts: AccountGroup[],
  isRegionSwitcherUsingGetCountries: boolean,
):
  | [RegionSwitcherAccount | undefined, RegionSwitcherAccount[] | undefined]
  | [Account | undefined, Account[] | undefined] =>
  isRegionSwitcherUsingGetCountries
    ? findCurrentAccountsCountriesController(
        countryListAccounts,
        currentMarketplaceId,
      )
    : findCurrentAccountNavStateController(
        currentEntityId,
        currentMarketplaceId,
        navStateAccounts,
      );

const transformRegionSwitcherAccounts = (
  regionSwitcherAccounts: CountriesList,
): RegionSwitcherAccount[] => {
  const accounts: RegionSwitcherAccount[] = [];
  Object.entries(regionSwitcherAccounts).forEach(([key, account]) => {
    const { url, regionId, marketplaceId, cfid } = account;
    // If url or regionId are undefined then object should not be passed to the regionSwitcherSelector
    if (url && regionId) {
      accounts.push({
        entityId: key,
        url,
        regionId: regionId as Region,
        cfid,
        marketplaceId,
      });
    }
  });
  return accounts;
};

export const RegionSwitcherProvider: React.FC = ({ children }) => {
  const [accounts, setAccounts] = useState<CountriesList>({});
  const isRegionSwitcherUsingGetCountries = useIsFeatureEnabled(
    WeblabedFeatures.CONTEXT_SERVICE_ADSP_GLOBAL_ACCOUNT,
  );
  const accountDropdownContext = useAccountDropdownContext();
  const navStateAccounts = accountDropdownContext.accounts;
  const { currentEntityId, currentMarketplaceId } = accountDropdownContext;
  const [currentAccount, regionalAccounts] = determineAccounts(
    accounts,
    currentMarketplaceId,
    currentEntityId,
    navStateAccounts,
    isRegionSwitcherUsingGetCountries,
  );

  // Ajax load region switcher state from regionSwitcherState api
  const fetchRegionSwitcherStateInfo = async () => {
    try {
      // Checks is /advertisers/{cfid}/ is in the url and pulls out the cfid
      const urlMatch = window.location.pathname.match(/advertisers\/([^/]+)/);
      const cfid = urlMatch ? urlMatch[1] : undefined;
      const regionSwitcherState = await fetchCountriesList({}, cfid);
      setAccounts(regionSwitcherState);
      RegionSwitcherPublisher.publishCounterMonitor(
        'regionSwitcherFailure-fetchRegionSwitcherStateInfo',
        0,
      );
    } catch (e: any) {
      RegionSwitcherPublisher.publishCounterMonitor(
        'regionSwitcherFailure-fetchRegionSwitcherStateInfo',
        1,
      );
      logger.error('fetchRegionSwitcherStateInfo failed', e);
    }
  };

  useEffect(() => {
    if (isRegionSwitcherUsingGetCountries) fetchRegionSwitcherStateInfo();
  }, [isRegionSwitcherUsingGetCountries]);

  return (
    <RegionSwitcherContext.Provider
      // Two genuine, pre-existing discrepancies are reconciled here rather than
      // papered over blindly:
      //   1. `undefined` — neither controller initialises its accumulators, so a
      //      no-match returns [undefined, undefined]. The context type never
      //      admitted `undefined`; the consumer (RegionSwitcher.tsx) already
      //      guards `if (!accounts || !currentAccount ...) return null`, so the
      //      `?? []` / `?? null` fallbacks here match that runtime intent.
      //   2. Shape — the deprecated NavState path (findCurrentAccountNavState-
      //      Controller, see its TODO) yields library `Account` objects, not
      //      `RegionSwitcherAccount`. The region-switcher selector consumes them
      //      structurally (its own Account type is not exported), so both shapes
      //      work at runtime. Fully typing the context as a union of both shapes
      //      is a product decision tied to retiring the NavState path.
      value={{
        accounts:
          (regionalAccounts as RegionSwitcherAccount[] | undefined) ?? [],
        currentAccount:
          (currentAccount as RegionSwitcherAccount | undefined) ?? null,
      }}
    >
      {children}
    </RegionSwitcherContext.Provider>
  );
};
